// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC. 

/* eslint-disable spire/avoid-dynamic-translate */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import Clickable from "@insite/mobius/Clickable";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Select from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AchForm from "@rugby/App/Components/Ach/AchForm";
import Notify from "@rugby/App/Components/Notify";
import getAchBankName from "@rugby/App/Handlers/XngAch/AchBankName";
import AddAch from "@rugby/App/Handlers/XngAch/AddAch";
import LoadAch from "@rugby/App/Handlers/XngAch/LoadAch";
import { AchParameter } from "@rugby/App/Services/AchService";
import { xAchState } from "@rugby/App/Store/Reducers";
import { getAchs, getIsloading } from "@rugby/App/Store/Selectors/AchSelectors";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps {}

const mapStateToProps = (state: ApplicationState & xAchState) => {
    const achs = (getAchs(state) || []) as AchParameter[];
    return {
        achs,
        hasAch: !!achs?.length,
        isLoading: getIsloading(state),
    };
};

const mapDispatchToProps = {
    LoadAch,
    AddAch,
    getAchBankName,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ACHStyles {
    accountWrapper?: InjectableCss;
    useForFuture?: InjectableCss;
    buttonWrapper?: InjectableCss;
    contactUsWrapper?: InjectableCss;
}

export const ACHStyles: ACHStyles = {
    accountWrapper: {
        css: css`
            width: 100%;
            margin-top: 8px;
        `,
    },
    useForFuture: {
        css: css`
            margin-top: 16px;
        `,
    },
    buttonWrapper: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            margin-top: 16px;
        `,
    },
    contactUsWrapper: {
        css: css`
            span,
            button {
                display: inline-block;
            }

            button {
                color: white;
                margin-left: 4px;
                text-decoration: underline;
            }
        `,
    },
};

const styles = ACHStyles;

const Ach: FC<Props> = ({ achs, hasAch, isLoading, LoadAch, AddAch, getAchBankName }) => {
    const [useForFuture, setUseForFuture] = useState(false);
    const [addNewACH, setAddNewACH] = useState(false);
    const [selectedAch, setSelectedAch] = useState({} as any);
    const [preSelectedAch, setPreSelectedAch] = useState({} as any);
    const [preSelectedAccountId, setPreSelectedAccountId] = useState("");
    const dispatch = useDispatch();

    useEffect(() => {
        LoadAch({});
    }, []);

    useEffect(() => {
        let ach;
        if (achs.length === 1) {
            ach = achs[0];
        } else if (achs.length > 0) {
            ach = preSelectedAccountId
                ? achs.find(ach => ach.accountNumber === preSelectedAccountId)
                : achs.find(ach => ach.approvedForACHProcessing);
        }
        if (ach) {
            setSelectedAch(ach);
            setPreSelectedAch(ach);
        }
    }, [achs]);

    useEffect(() => {
        if (selectedAch?.accountNumber) {
            dispatch({ type: "Data/ach/selectedAch", selectedAch });
            dispatch({
                type: "Data/ach/AchError",
                achError: "",
            });
        }
    }, [selectedAch]);

    const onAccountNumberChange = (value: any) => {
        const ach = achs.find(ach => ach.accountNumber === value);
        setSelectedAch(ach);
        setPreSelectedAch(ach);

        if (useForFuture) {
            setUseForFuture(!useForFuture);
            dispatch({
                type: "Data/ach/IsEditing",
                isEditing: !useForFuture,
            });
        }

        dispatch({
            type: "Data/ach/AchError",
            achError: "",
        });
    };

    const addNewHandler = (event: any) => {
        event.preventDefault();
        if (useForFuture) {
            setUseForFuture(!useForFuture);
            dispatch({
                type: "Data/ach/IsEditing",
                isEditing: !useForFuture,
            });
            return;
        }

        if (!addNewACH) {
            setSelectedAch({});
            dispatch({ type: "Data/ach/selectedAch", selectedAch: undefined });
            dispatch({ type: "Data/ach/bankName", selectedBankName: "" });
        } else {
            setSelectedAch(preSelectedAch || achs.find(ach => ach.approvedForACHProcessing));
        }
        setAddNewACH(!addNewACH);
    };

    const clearAllStatus = (accountNumber?: string) => {
        setPreSelectedAccountId(accountNumber || "");
        setUseForFuture(false);
        setAddNewACH(false);
        setSelectedAch({});
        dispatch({ type: "Data/ach/selectedAch", selectedAch: {} });
        dispatch({
            type: "Data/ach/IsEditing",
            isEditing: false,
        });
        LoadAch({});
    };

    const openChatPopup = (event: any) => {
        event.preventDefault();
        (window as any).acquireIO.ui.status("max");
    };

    return (
        <LoadingOverlay loading={isLoading}>
            {hasAch && !addNewACH && (
                <StyledWrapper {...styles.accountWrapper}>
                    {achs.length === 1 ? (
                        <TextField
                            label={translate("Account")}
                            value={selectedAch?.accountNumberLabel}
                            data-test-selector="account_Number"
                            readOnly
                            disabled={true}
                        />
                    ) : (
                        <Select
                            label={translate("Account")}
                            value={selectedAch?.accountNumber}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                                onAccountNumberChange(event.target.value)
                            }
                            data-test-selector="account_number"
                        >
                            <option value="">
                                {translate("Select Account Number")}
                            </option>
                            {achs.map(ach => (
                                <option key={ach.accountNumber} value={ach.accountNumber}>
                                    {ach.accountNumberLabel}
                                </option>
                            ))}
                        </Select>
                    )}
                    <StyledWrapper {...styles.useForFuture}>
                        <Checkbox
                            checked={useForFuture || selectedAch?.approvedForACHProcessing}
                            disabled={!selectedAch?.accountNumber || selectedAch?.approvedForACHProcessing}
                            onChange={() => {
                                dispatch({
                                    type: "Data/ach/IsEditing",
                                    isEditing: !useForFuture,
                                });
                                setUseForFuture(!useForFuture);
                            }}
                        >
                            {translate("Use for future orders")}
                        </Checkbox>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {(!hasAch || useForFuture || addNewACH) && (
                <AchForm
                    defualtApprovedforACH={useForFuture}
                    initialAch={selectedAch}
                    getBankNameCallback={getAchBankName}
                    addAchCallback={AddAch}
                    onSuccessCallback={clearAllStatus}
                />
            )}
            {hasAch && (
                <StyledWrapper {...styles.buttonWrapper}>
                    <Button data-test-selector="add_new_ACH" onClick={(event: any) => addNewHandler(event)}>
                        {addNewACH || useForFuture ? translate("Cancel") : translate("Add new Bank")}
                    </Button>
                </StyledWrapper>
            )}
            <Notify
                style={{ marginTop: "20px" }}
                messageType="info"
                body={
                    <StyledWrapper {...styles.contactUsWrapper}>
                        <Typography>{translate("If you need to delete a bank, please")}</Typography>
                        <Clickable onClick={openChatPopup}>{translate("contact us")}</Clickable>
                    </StyledWrapper>
                }
            />
            {addNewACH && (
                <Notify
                style={{ marginTop: "20px" }}
                messageType="info"
                body={
                    <StyledWrapper {...styles.contactUsWrapper}>
                        <Typography>{siteMessage("Rugby_InvoicePayments_AddNewACH")}</Typography>
                    </StyledWrapper>
                }
                />
            )}
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(Ach);
